import { useEffect, useState } from 'react';
import { IDaySchedule, createDefaultWeekSchedule } from './DayScheduleEditor';

/** Minimal schedule shape accepted by weeklySchedulesToDays — covers
 *  both IDayScheduleView (businessService) and IWeeklyScheduleDay (employeeService). */
export interface IScheduleInput {
  dayOfWeek: number;
  isClosed: boolean;
  startTime?: string | null;
  endTime?: string | null;
}

/** Converts API schedule array to IDaySchedule[], falling back to defaults when empty/null. */
export function weeklySchedulesToDays(
  schedules?: IScheduleInput[] | null
): IDaySchedule[] {
  if (schedules && schedules.length > 0) {
    return schedules.map(s => ({
      dayOfWeek: s.dayOfWeek,
      isClosed: s.isClosed,
      startTime: s.startTime ?? null,
      endTime: s.endTime ?? null,
    }));
  }
  return createDefaultWeekSchedule();
}

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

/**
 * Validates schedule days, returning an error message or null if valid.
 * Checks: open days must have start < end.
 */
export function validateScheduleDays(days: IDaySchedule[]): string | null {
  for (const day of days) {
    if (day.isClosed) continue;
    if (day.startTime && day.endTime && day.startTime >= day.endTime) {
      return `O horário de início (${day.startTime}) deve ser antes do horário de término (${day.endTime}) na ${DAY_NAMES[day.dayOfWeek]}.`;
    }
  }
  return null;
}

/**
 * Manages IDaySchedule[] state synced with an API schedule source.
 *
 * - On mount: initializes from `schedules` (or defaults if null/empty).
 * - Whenever `schedules` changes: re-syncs (handles async-loaded data).
 *
 * Use in place of raw useState + useEffect patterns for schedule editors.
 */
export function useScheduleState(
  schedules?: IScheduleInput[] | null
): [IDaySchedule[], React.Dispatch<React.SetStateAction<IDaySchedule[]>>] {
  const [days, setDays] = useState<IDaySchedule[]>(() =>
    weeklySchedulesToDays(schedules)
  );

  // Re-sync when API data arrives (async) or changes
  useEffect(() => {
    setDays(weeklySchedulesToDays(schedules));
  }, [schedules]);

  return [days, setDays];
}
